import { ChangeDetectorRef, Component } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import moment from 'moment';
import { take } from 'rxjs';
import { ITableResponsive } from 'src/app/domain/ITableResponsive';
import { MessagesEnum } from 'src/app/domain/enums/messages.enum';
import { getSystemTypeDescriptionByValue } from 'src/app/domain/registration/enums/system-type.enum';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { TypeComponentFieldEnum } from 'src/app/domain/registration/enums/type-component-dynamic.enum';
import { ITraining } from 'src/app/domain/registration/models/ITraining';
import { AbstractCrudIndex } from 'src/app/shared/components/crud/abstract-crud-index';
import { DialogConfirmComponent } from 'src/app/shared/components/dialog-confirm/dialog-confirm.component';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { PermissionService } from 'src/app/shared/permission.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { S3_URL_BASE } from 'src/environments/environment';
import { DownloadProgressDialogComponent } from '../../downloads/download-progress-dialog-component/download-progress-dialog-component.component';
import { TrainingCESComponent } from '../crud-ces/crud-ces.component';
import { FormDataCreateTrainings } from '../crud-ces/formDataBuild/form-data-create';
import {
  COLUMNS_TO_SHOW_MOBILE,
  COLUMNS_TO_SHOW_WEB,
  TABLE_CONFIG_COLUMNS,
} from '../crud-config';
import { TrainingService } from '../crud-service';
import { FileDescriptionComponent } from '../file-description/file-description.component';
import { FileViewerDialogComponent } from '../file-viewer-dialog/file-viewer-dialog.component';
import { UnsupportedFileDialogComponent } from '../unsupported-file-dialog/unsupported-file-dialog.component';

@Component({
  selector: 'app-training-index',
  templateUrl: './crud-index.component.html',
  styleUrls: ['./crud-index.component.sass'],
  providers: [TrainingService],
})
export class TrainingCrudIndex
  extends AbstractCrudIndex<ITraining>
  implements ITableResponsive {
  override title = 'TREINAMENTOS';
  override displayedColumns = COLUMNS_TO_SHOW_WEB;
  override configColumns = TABLE_CONFIG_COLUMNS;

  loadingDownloads: { [key: string]: boolean } = {};
  loadingProgress: { [key: string]: number } = {};
  currentRequest: XMLHttpRequest | null = null;

  currentTab: string = ''; 
  userType: string = 'admin'; 
  public filterFormValues: any = null
  constructor(
    public override dialog: MatDialog,
    public override service: TrainingService,
    public override toastService: ToastService,
    public override mediaObserver: MediaObserver,
    public override router: Router,
    protected cdr: ChangeDetectorRef,
    protected authService: AuthService, 
    public permissionService: PermissionService,
  ) {
    super(dialog, service, toastService, mediaObserver, router);

    this.userType = this.authService.isPartner() ? 'partner' : 'admin';
    this.currentTab = 'trainings';

    mediaObserver.asObservable().subscribe((res) => {
      this.is_mobile = res[0].mqAlias === 'sm' || res[0].mqAlias === 'xs';
      this.displayedColumns = this.is_mobile
        ? COLUMNS_TO_SHOW_MOBILE
        : COLUMNS_TO_SHOW_WEB;
    });
  }

  private formatFileSize(size: number): string {
    if (size >= 1024 * 1024) {
      return (size / (1024 * 1024)).toFixed(2) + ' MB';
    } else if (size >= 1024) {
      return (size / 1024).toFixed(2) + ' KB';
    } else {
      return size + ' B';
    }
  }

  ngAfterViewInit(): void {
    this.loadList();
  }

  override loadList() {
    let params = this.params;

    this.service.listAll(params).subscribe((dataWithPagination: any) => {
      const [data, length] = dataWithPagination;
      this.length = length;

      this.dataSource = this.getDataSource(data);

    });
  }

  handleTrainingFilter(filterData: any) {
    this.params.page = 0;

    if (filterData.searchTerm) {
      this.params.filter = filterData.searchTerm.toUpperCase();
    } else {
      this.params.filter = '';
    }

    if (filterData.typeFilter || filterData.systemTypeFilter) {
      const advancedFilter: any = {};

      if (filterData.typeFilter) {
        advancedFilter['fileType'] = filterData.typeFilter;
      }

      if (filterData.systemTypeFilter) {
        advancedFilter['type'] = filterData.systemTypeFilter;
      }

      this.lastAppliedFilter = advancedFilter;
      this.isAdvancedFilterActive = true;

      this.submitAdvancedFilter({ value: advancedFilter });
    } else {
      this.isAdvancedFilterActive = false;
      this.lastAppliedFilter = null;

      this.loadList();
    }
  }

  override changePage(event: any) {
    this.params.page = event.pageIndex || event.page;
    this.params.limit = event.pageSize || event.limit;

    if (this.isAdvancedFilterActive && this.lastAppliedFilter) {
      this.submitAdvancedFilter({ value: this.lastAppliedFilter } as any);
    } else {
      this.loadList();
    }
  }

  override submitAdvancedFilter(filterForm: any) {
    let _filterForm = this.getFilters(filterForm.value);

    this.service
      .findByFilter(_filterForm, this.params)
      .subscribe((dataWithPagination: any) => {
        let data = dataWithPagination[0];
        this.length = dataWithPagination[1];

        this.dataSource = this.getDataSource(data);
      });
  }

  private getFilters(filter: any) {
    let params: any = {};

    if (filter.trainingName) params.trainingName = filter.trainingName.trim().toUpperCase();
    if (filter.fileType) params.fileType = filter.fileType;
    if (filter.type) params.type = filter.type;

    return params;
  }

  public getDataSource(data: any): MatTableDataSource<any> {
    return new MatTableDataSource<any>(
      data.map((item: any) => {
        item['type'] = getSystemTypeDescriptionByValue(item.type);
        item['image'] = item.system?.image;
        item['system'] = item.system;
        item['systemName'] = item.system?.name || '--';
        item['createdAt'] = moment(item.createdAt).format('DD/MM/YYYY HH:mm');
        item['fileType'] = item.fileType || 'Tipo de arquivo não definido';
        item['systemVersion'] = item.systemVersion || 'Versão do sistema não definida';
        item['fileAttachment'] = item.fileAttachment;
        item['fileSize'] = this.formatFileSize(item.fileSize); // Adiciona o tamanho do arquivo formatado
        item['hideEdit'] = !item.allowEdit;
        item['hideDelete'] = !item.allowDelete;
        return item;
      })
    );
  }

  openShow(element: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      dialogConfig: {
        width: '75%',
        maxWidth: '900px',
        maxHeight: '85vh',
        height: '85vh'
      },
      component: TrainingCESComponent,
      data: { type: TypeCesEnum.SHOW, element },
      callBack: (data: any) => {
        if (data?.changeType === TypeCesEnum.EDIT) {
          this.openEdit(element);
        }

        if (data?.result) {
          this.openDelete(element);
        }
      },
    });
  }

  openNew(event: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      dialogConfig: {
        width: '75%',
        maxWidth: '900px',
        maxHeight: '85vh',
        height: '85vh'
      },
      component: TrainingCESComponent,
      data: { type: TypeCesEnum.NEW, element: null },
      callBack: async (data: any) => {
        if (data?.element) {
          const name = data.element.name ? data.element.name.toUpperCase() : '';
          const dueDate = data.element.dueDate ? data.element.dueDate : null;

          this.service
            .createWithFormData(
              await new FormDataCreateTrainings().build({
                ...data.element,
                name,
                dueDate,
              })
            )
            .subscribe(
              (response: any) => {
                this.loadList();
                this.toastService.showSuccess(MessagesEnum.SUCCESS_CREATED_TRAINING);
              },
              (error) => {
                this.toastService.showError(MessagesEnum.ERROR_TO_CREATE);
                this.openNew(data.element);
              }
            );
        }
      },
    });
  }

  openEdit(element: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      dialogConfig: {
        width: '75%',
        maxWidth: '900px',
        maxHeight: '85vh',
        height: '85vh'
      },
      component: TrainingCESComponent,
      data: { type: TypeCesEnum.EDIT, element },
      callBack: async (data: any) => {
        if (data?.element?.id) {
          const name = data.element.name ? data.element.name.toUpperCase() : '';
          const dueDate = data.element.dueDate ? data.element.dueDate : null;

          this.service
            .updateWithFormData(
              data.element.id,
              await new FormDataCreateTrainings().build({
                ...data.element,
                name,
                dueDate,
              })
            )
            .subscribe({
              next: () => {
                this.loadList();
                this.toastService.showSuccess(MessagesEnum.SUCCESS_UPDATED_TRAINING);
              },
              error: (error) => {
                this.openEdit(data.element);
              },
            });
        }

        if (data?.changeType === TypeCesEnum.SHOW) {
          this.openShow(element);
        }

        if (data?.result) {
          this.openDelete(element);
        }
      },
    });
  }

  handlePlay(element: any) {
    const fileAttachment = element.fileAttachment;
    if (fileAttachment) {
      const fileExtension = fileAttachment.split('.').pop()?.toLowerCase();
      const completeUrl = S3_URL_BASE + fileAttachment;

      if (['mp4', 'jpg', 'png', 'pdf','webp','jpeg'].includes(fileExtension)) {
        this.dialog.open(FileViewerDialogComponent, {
          data: {fileUrl : completeUrl, onlyLink: true},
          width: '80%',
        });
      } else if (fileExtension === 'mkv') {
        const dialogRef = this.dialog.open(FileViewerDialogComponent, {
          data: { videoUrl: completeUrl, fileType: 'video/mkv' },
          width: '80%',
        });
      } else {
        this.dialog.open(UnsupportedFileDialogComponent, {
          width: '30%',
        });
      }
    } else {
      this.toastService.showError('Arquivo não encontrado.');
    }
  }

  downloadFile({ fileUrl, element }: { fileUrl: string; element: any }): void {
    if (fileUrl) {
      const completeUrl = S3_URL_BASE + fileUrl;

      this.loadingDownloads[element.id] = true;
      this.loadingProgress[element.id] = 0;

      const xhr = new XMLHttpRequest();
      this.currentRequest = xhr;

      const dialogRef = this.dialog.open(DownloadProgressDialogComponent, {
        disableClose: true,
        width: '400px',
        data: { progress: 0 },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'cancel') {
          this.cancelDownload(element);
        }
      });

      xhr.open('GET', completeUrl, true);
      xhr.responseType = 'blob';

      xhr.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          this.loadingProgress[element.id] = progress;
          dialogRef.componentInstance.updateProgress(progress);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          const blob = xhr.response;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', this.getFileNameFromUrl(fileUrl));
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          this.toastService.showError('Falha ao baixar o arquivo.');
        }
      };

      xhr.onerror = () => {
        this.toastService.showError('Erro ao baixar o arquivo.');
      };

      xhr.onloadend = () => {
        this.loadingDownloads[element.id] = false;
        this.loadingProgress[element.id] = 0;
        this.currentRequest = null;
        dialogRef.close();
      };

      xhr.send();
    } else {
      this.toastService.showError('Arquivo não encontrado.');
    }
  }

  cancelDownload(element: any): void {
    if (this.currentRequest) {
      this.currentRequest.abort();
      this.toastService.showInfo(`Download cancelado para o arquivo: ${element.fileAttachment}`);
      this.loadingDownloads[element.id] = false;
      this.loadingProgress[element.id] = 0;
      this.currentRequest = null;
    }
  }

  showDescriptionFile(element: any): void {
    const dialogRef = this.dialog.open(FileDescriptionComponent, {
      width: '400px',
      data: element
    });

    dialogRef.afterClosed().subscribe(result => {
    });
  }

  private getFileNameFromUrl(url: string): string {
    return url.substring(url.lastIndexOf('/') + 1);
  }

  public lock(training: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: `Bloquear training => sistema: ${training.system}, terminais: ${training.terminalQuantity}` },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.service
            .changeActiveStatus(training.id!, false)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showSuccess(MessagesEnum.PLAN_LOCK_SUCCESS);
                this.loadList();
              },
              error: (e) => console.log(e),
            });
        }
      },
    });
  }

  public unlock(training: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: `Desbloquear training => sistema: ${training.name}, terminais: ${training.terminalQuantity}` },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.service
            .changeActiveStatus(training.id!, true)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showSuccess(
                  MessagesEnum.PLAN_UNLOCK_SUCCESS
                );
                this.loadList();
              },
              error: (e) => console.log(e),
            });
        }
      },
    });
  }

  public resetFilter(): void {
    this.filterFormValues = null;
    localStorage.removeItem('parcllilterFormValues');

    this.isAdvancedFilterActive = false;
    this.lastAppliedFilter = null;
    this.params = { limit: 12, page: 0, filter: '' };

    const resetFields = this.filterFields.map(field => {
      if (field.type === TypeComponentFieldEnum.SLIDE_TOGGLE) {
        return { ...field, value: false };
      }
      return { ...field, value: '' };
    });

    this.filterFields = resetFields;

    this.loadList();
  }

  public resetTrainingFilters(): void {
    this.isAdvancedFilterActive = false;
    this.lastAppliedFilter = null;
    this.params = { limit: 12, page: 0, filter: '' };

    this.loadList();
  }
}
